import { useSnackbar } from 'notistack';
import { useEffectOnChange } from 'utils/useEffectOnChange';

type Options = {
  successMessage?: string;
  successVariant?: 'success' | 'warning';
  errorMessage?: string;
  onSuccess?: () => void;
  onError?: () => void;
};

function useHandleApiResponse(
  response: { loading: boolean; error?: { message: string } },
  {
    successMessage,
    successVariant = 'success',
    errorMessage,
    onSuccess,
    onError,
  }: Options = {},
) {
  const snackbar = useSnackbar();

  useEffectOnChange(
    () => {
      if (response.error?.message === 'Token has expired') {
        return;
      }
      if (response.error && errorMessage !== null) {
        snackbar.enqueueSnackbar(errorMessage || response.error.message, {
          variant: 'error',
        });
        if (onError) onError();
      } else {
        if (successMessage) {
          snackbar.enqueueSnackbar(successMessage, {
            variant: successVariant,
          });
        }
        if (onSuccess) onSuccess();
      }
    },
    response.loading,
    false,
  );
}

/**
 * A bulk send that went through but skipped some voices answers with a
 * message saying who and why; show that (as a warning) instead of the
 * plain success message.
 */
export const sentOrSkipped = (
  data: { message?: string } | undefined,
  sent: string,
): Pick<Options, 'successMessage' | 'successVariant'> =>
  data?.message
    ? { successMessage: data.message, successVariant: 'warning' }
    : { successMessage: sent };

/**
 * A failed save's message. When the server answered, it says why (and
 * nothing was saved); when it couldn't be reached (no status code), the
 * change may still have been saved, so it doesn't claim otherwise.
 */
export const notSaved = (
  prefix: string,
  error?: { message?: string; code?: number },
) =>
  error && !error.code
    ? "Couldn't reach the server, so this may or may not have been saved. Reload the page to check."
    : withReason(prefix, error?.message);

/** "Delete failed: <the API's reason>", or "Delete failed." without one. */
export const withReason = (prefix: string, reason?: string) =>
  reason ? `${prefix}: ${reason}` : `${prefix}.`;

export default useHandleApiResponse;
